import React, { useRef, useState } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { GripVerticalIcon, PencilIcon } from 'lucide-react';
import { ProfileIcon } from '../../../icons/ProfileIcons';
import { IconPicker } from './IconPicker';
import type { ProfileIconId, SupportProfile } from '../../../../types/supportProfiles';
type ProfileCardProps = {
  profile: SupportProfile;
  onRename: (name: string) => void;
  onIconChange: (icon: ProfileIconId) => void;
};
export function ProfileCard({
  profile,
  onRename,
  onIconChange
}: ProfileCardProps) {
  const controls = useDragControls();
  const iconRef = useRef<HTMLButtonElement>(null);
  const [picker, setPicker] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(profile.name);
  const startEdit = () => {
    setDraft(profile.name);
    setEditing(true);
  };
  const commit = () => {
    const next = draft.trim();
    if (next && next !== profile.name) onRename(next);
    setEditing(false);
  };
  return <Reorder.Item value={profile.id} dragListener={false} dragControls={controls} className="relative list-none rounded-[9px]" whileDrag={{
    scale: 1.02,
    boxShadow: '0 10px 28px rgba(20,28,38,0.18)',
    zIndex: 10
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} data-id="element-560" data-mp="1vc3ohrfeiih9">
      
      <div className="flex items-center gap-[10px] rounded-[9px] border border-pf-line bg-white py-[9px] pl-[4px] pr-[12px]" data-id="element-561" data-mp="1zbuazixwacfz">
        <button type="button" aria-label={`Drag to reorder ${profile.name}`} onPointerDown={e => {
        e.preventDefault();
        controls.start(e);
      }} className="flex h-[32px] w-[20px] shrink-0 cursor-grab touch-none items-center justify-center rounded-[5px] text-pf-faint transition-colors duration-150 hover:bg-pf-panel hover:text-pf-ink active:cursor-grabbing" data-id="element-562" data-mp="29bcqnn6rvc28">
          
          <GripVerticalIcon className="h-[15px] w-[15px]" strokeWidth={2.2} data-id="element-563" data-mp="2s2vx64009890" />
        </button>
        <button ref={iconRef} type="button" aria-label={`Change icon for ${profile.name}`} aria-expanded={picker} onClick={() => setPicker(o => !o)} className={`flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[8px] border transition-colors duration-150 ${picker ? 'border-pf-blue bg-pf-blue-soft text-pf-blue' : 'border-pf-line bg-pf-panel text-pf-ink hover:border-pf-blue-line hover:text-pf-blue'}`} data-id="element-564" data-mp="1ppg4pdbqzlyd">
          
          <ProfileIcon icon={profile.icon} className="h-[18px] w-[18px]" data-id="element-565" data-mp="2dcftzuhrb9kz" />
        </button>
        <div className="min-w-0 flex-1" data-id="element-566" data-mp="2hgvybrbq28y3">
          {editing ? <input autoFocus aria-label="Profile name" value={draft} maxLength={40} onChange={e => setDraft(e.target.value)} onBlur={commit} onKeyDown={e => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            e.stopPropagation();
            setEditing(false);
          }
        }} onFocus={e => e.target.select()} className="h-[24px] w-full rounded-[5px] border border-pf-blue bg-white px-[6px] text-[12.5px] font-semibold text-pf-ink outline-none ring-2 ring-pf-blue/15" data-id="element-567" data-mp="2mefg317mp3us" /> : <button type="button" onClick={startEdit} aria-label={`Rename ${profile.name}`} className="group flex h-[24px] max-w-full items-center gap-[6px] rounded-[5px] text-left" data-id="element-568" data-mp="5u27m8nv7vem">
              <span className="truncate text-[12.5px] font-semibold text-pf-ink" data-id="element-569" data-mp="yhfybp0yzuqa">{profile.name}</span>
              <PencilIcon className="h-[12px] w-[12px] shrink-0 text-pf-muted opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100" strokeWidth={2.4} data-id="element-570" data-mp="tg8qjpytuvjm" />
            
            </button>}
          <p className="truncate text-[11px] text-pf-muted" data-id="element-571" data-mp="3a67fs2lqq79d">{profile.description}</p>
        </div>
      </div>
      <IconPicker anchorRef={iconRef} open={picker} onClose={() => setPicker(false)} value={profile.icon} onSelect={id => {
      onIconChange(id);
      setPicker(false);
    }} data-id="element-572" data-mp="11wpus1qoxwke" />
      
    </Reorder.Item>;
}